import Vue from 'vue'
import Router from 'vue-router'
import axios from './axios'

import store from './store/store'
Vue.use(Router)

import clientRoutes from '@/views/clients/routes'
import patientRoutes from '@/views/patients/routes'
import supplierRoutes from '@/views/suppliers/routes'
import userRoutes from '@/views/users/routes'
import roleRoutes from '@/views/roles/routes'
import transporterRoutes from '@/views/transporters/routes'
import expenseCategoryRoutes from '@/views/expense-category/routes'
import expenseRoutes from '@/views/expense/routes'
import batchRoutes from '@/views/batches/routes'
import inventoryRoutes from '@/views/inventories/routes'
import productRoutes from '@/views/products/routes'
import settingRoutes from '@/views/settings/routes'
import labRoutes from '@/views/labs/routes'
import saleRoutes, { blankRoutes as blankPosRoutes, posRoutes } from '@/views/pos/routes'
import marketplaceRoutes from '@/views/marketplace/routes'
import labTestRoutes, { blankRoutes as blankLabTestRoutes } from '@/views/lab-tests/routes'
import labAdminRoutes from '@/views/lab-admin/routes'
import strainRoutes from '@/views/strains/routes'
import reportsRoutes from '@/views/reports/routes'
import contactusRoutes from '@/views/contactus/routes'
import pricingRoutes from '@/views/pricing-group/routes'
import cashDropRoutes from '@/views/cash-drop/routes'
import registerRoutes from '@/views/registers/routes'
import cashFloatRoutes from '@/views/cash-float/routes'
import punchClockRotues from '@/views/punch-clock/routes'
import locationRoutes from '@/views/location/routes'
import promoDiscountRoutes from '@/views/promo-discount/routes'
import seedsuiteTagsRoutes from '@/views/seedsuite-tags/routes'
import transferRoutes from '@/views/transfers/routes'

import { MULTILOCATION } from '@/utils/roles'

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/term-and-conditions',
      name: 'auth-term-and-conditions',
      component: () => import('@/views/auth/TermAndConditions.vue'),
      meta: {
        allowAnonymous: true
      }
    },
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: '',
          redirect: '/login'
        },
        {
          path: '/logout',
          redirect: '/login'
        },
        {
          path: '/login',
          name: 'auth-login',
          component: () => import('@/views/auth/Login.vue'),
          meta: {
            allowAnonymous: true
          }
        },
        {
          path: '/forget-password',
          name: 'auth-forget-password',
          component: () => import('@/views/auth/ForgotPassword.vue'),
          meta: {
            allowAnonymous: true
          }
        },

        {
          path: '/restore-password',
          name: 'auth-restore-password',
          component: () => import('@/views/auth/RestorePassword.vue'),
          meta: {
            allowAnonymous: true
          }
        },

        {
          path: '/pages/404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue')
        }
      ]
    },
    {
      path: '/',
      component: () => import('@/layouts/main/Main.vue'),
      children: [
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            allowAnonymous: false,
            pageTitle: 'Dashboard',
            breadcrumb: [{ title: 'Dashboard', active: true }]
          }
        },
        {
          path: '/profile',
          name: 'profile',
          component: () => import('@/views/users/Profile.vue'),
          meta: {
            allowAnonymous: false,
            pageTitle: 'Profile',
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Profile', active: true }
            ]
          }
        },
        ...supplierRoutes,
        ...userRoutes,
        ...roleRoutes,
        ...clientRoutes,
        ...patientRoutes,
        ...transporterRoutes,
        ...expenseCategoryRoutes,
        ...expenseRoutes,
        ...batchRoutes,
        ...strainRoutes,
        ...inventoryRoutes,
        ...productRoutes,
        ...settingRoutes,
        ...saleRoutes,
        ...labRoutes,
        ...labTestRoutes,
        ...labAdminRoutes,
        ...reportsRoutes,
        ...contactusRoutes,
        ...pricingRoutes,
        ...cashDropRoutes,
        ...registerRoutes,
        ...cashFloatRoutes,
        ...punchClockRotues,
        ...locationRoutes,
        ...promoDiscountRoutes,
        ...seedsuiteTagsRoutes,
        ...transferRoutes
      ]
    },

    // POS route
    {
      path: '/pos',
      component: () => import('@/layouts/PosLayOut.vue'),
      children: [...posRoutes]
    },
    {
      path: '/',
      component: () => import('@/layouts/Blank.vue'),
      children: [...blankPosRoutes]
    },

    // marketplace route
    {
      path: '/',
      component: () => import('@/layouts/Blank.vue'),
      children: [...marketplaceRoutes]
    },

    // Lab Test Route (PDF)
    {
      path: '/',
      component: () => import('@/layouts/Blank.vue'),
      children: [...blankLabTestRoutes]
    },

    {
      path: '*',
      redirect: '/pages/404'
    }
  ]
})
const isAuthenticated = async () => {
  const accessToken = localStorage.getItem('accessToken')
  if (accessToken && !store.state.auth.user) {
    try {
      const res = await axios.get('/auth/profile')
      store.commit('auth/SET_USER_INFO', res.data.user)
      store.commit('auth/SET_PARENT_USER_INFO', res.data.parentUser)
      store.commit('auth/SET_PERMISSIONS', res.data.permissions)
      store.commit('auth/SET_GROWER_AND_PROCESSOR_ROLE_GROUP', res.data.growerAndProcessorRoleGroup)
      if (res.data.user.roleId === MULTILOCATION) {
        localStorage.setItem('logInAsMultiLocationUser', true)
        localStorage.setItem('MultiLocationUser', res.data.user.id)
      } else if (localStorage.getItem('logInAsMultiLocationUser') === 'true' && res.data.user.roleId !== MULTILOCATION) {
        localStorage.setItem('logInAsMultiLocationUser', false)
      }
      return true
    } catch (err) {
      return false
    }
  } else if (accessToken && store.state.auth.user) {
    return true
  }
  return false
}

router.beforeEach(async (to, from, next) => {
  if (to.name !== 'pos') {
    localStorage.removeItem('posCustomerId')
  }

  if (Object(to.meta).hasOwnProperty('allowAnonymous')) {
    const authed = await isAuthenticated()
    if (!to.meta.allowAnonymous && !authed) {
      store.dispatch('auth/logOut')
      return next({
        path: '/'
      })
    } else if (to.meta.allowAnonymous && authed) {
      return next({
        path: '/dashboard'
      })
    } else {
      if (!to.meta.allowAnonymous && to.meta && to.meta.permissions && !store.getters['auth/checkPermissionByIds'](to.meta.permissions || [])) {
        const app = window.document.getElementById('app')
        if (app && app.__vue__) {
          app.__vue__.$vs.notify({
            title: 'Error',
            text: `You don't have access to visit "${to.meta.pageTitle}"`,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        }
        return next({
          path: '/dashboard'
        })
      } else if (!to.meta.allowAnonymous && to.meta && to.meta.roles && !to.meta.roles.includes(store.state.auth.user.roleId)) {
        const app = window.document.getElementById('app')
        if (app && app.__vue__) {
          app.__vue__.$vs.notify({
            title: 'Error',
            text: `You don't have access to visit "${to.meta.pageTitle}"`,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        }
        return next({
          path: '/dashboard'
        })
      }
      return next()
    }
  }
  return next()
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
